import { useState } from "react";
import { useForm } from "react-hook-form";
import { forgotPassword } from "../../api/authApi";
import { useResendTimer } from "../../hooks/useResendTimer";

import InputLabel from "../Shared/InputLabel";
import Input from "../Shared/Input";
import Button from "./Button";
import CTA from "./CTA";
import FormWrapper from "./FormWrapper";

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const { canResend, timeLeft, startCooldown } = useResendTimer(120, 'forgot-password-cooldown');
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");
    
    try {
      await forgotPassword(data);
      setSubmittedEmail(data.email);
      startCooldown();
    } catch (error) {
      console.error(error);
      if (error.response?.status === 429) {
        setError("Too many requests. Please wait a few minutes before trying again.");
      } else if (error.response?.status >= 500) {
        setError("Server error. Please try again later.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !submittedEmail) return;
    
    setIsLoading(true);
    setError("");
    
    try {
      await forgotPassword({ email: submittedEmail });
      startCooldown();
    } catch (error) {
      console.error(error);
      if (error.response?.status === 429) {
        setError("Too many requests. Please wait before trying again.");
      } else {
        setError("Failed to resend email. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <CTA
        question="Remember your password?"
        linkText="Sign in to continue"
        page="signin"
      />
      <FormWrapper>
        <p className="text-base text-ink-700 mb-4">
          Enter your email address to receive password reset instructions.
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="p-3 bg-signal-red-50 border border-signal-red-100 rounded-md">
              <p className="text-sm text-signal-red-600">{error}</p>
            </div>
          )}
          <div>
            <InputLabel htmlFor="email">Email address</InputLabel>
            <Input
              id="email"
              name="email"
              errormessage={errors?.email?.message}
              readOnly={isSubmitSuccessful || isLoading}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Invalid email address",
                },
              })}
            />
          </div>
          {!isSubmitSuccessful ? (
            <>
              <div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-accent-500 hover:bg-accent-400 focus-visible:outline-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending..." : "Send reset link"}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-signal-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-signal-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-sm text-accent-700 font-medium mb-2">
                Reset instructions sent! Check your email within the next 5 minutes.
              </p>
              {submittedEmail && (
                <p className="text-xs text-ink-600 mb-2">
                  Instructions sent to: <span className="font-medium">{submittedEmail}</span>
                </p>
              )}
              <p className="text-xs text-ink-600 mb-2">
                Click the link in the email to create a new password.
              </p>
              <p className="text-xs text-ink-500 mb-4">
                Don't see the email? Check your spam folder or contact support.
              </p>
              <div className="pt-2 border-t border-ink-100">
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isLoading}
                    className="text-sm text-accent-600 hover:text-accent-500 disabled:opacity-50"
                  >
                    Resend email
                  </button>
                ) : (
                  <p className="text-xs text-ink-500">
                    Resend available in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </div>
            </div>
          )}
        </form>
      </FormWrapper>
    </div>
  );
};

export default ForgotPassword;
